import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, concatMap, take } from 'rxjs';
import { S3PresignedUrl } from 'src/app/classes/s3-presigned-url';

import { Contract } from '../classes/models';
import { AuthService } from './auth.service';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root'
})
export class ContractService {
  constructor(
    private http: HttpClient,
    private AuthService: AuthService,
    private ToastService: ToastService
  ) {}

  public getContract(query: {} = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<Contract>;
        } = {
          Client: () =>
            this.http.get<Contract>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/contracts/one`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        this.ToastService.addErrorToast('Error', 'Invalid permission.');
        throw new Error('Invalid permission.');
      })
    );
  }

  public getContracts(query: {} = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<Contract[]>;
        } = {
          Client: () =>
            this.http.get<Contract[]>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/contracts/many`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        this.ToastService.addErrorToast('Error', 'Invalid permission.');
        throw new Error('Invalid permission.');
      })
    );
  }

  public getContractCount(query: {} = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<number>;
        } = {
          Client: () =>
            this.http.get<number>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/contracts/count`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        this.ToastService.addErrorToast('Error', 'Invalid permission.');
        throw new Error('Invalid permission.');
      })
    );
  }

  public saveContract(
    Contract: Contract,
    query: {} = {}
  ): Observable<Contract> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Client: () =>
            this.http.post(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/contracts/${Contract.id}`,
              Contract,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  public deleteContract(ContractId: number, query: {} = {}): Observable<any> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Client: () =>
            this.http.delete(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/contracts/${ContractId}`
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  public deleteContractFile(
    ContractId: number,
    FileId: number,
    query: {} = {}
  ): Observable<any> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Client: () =>
            this.http.delete(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/contracts/${ContractId}/file/${FileId}/delete`
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  public getContractFrequencies(): Observable<any> {
    return this.http.get('/api/v2/public/data/frequencies');
  }

  public getContractFileS3PresignedUrl = (
    ContractId: number,
    FileId: number
  ) => {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<S3PresignedUrl>;
        } = {
          Client: () =>
            this.http.get<S3PresignedUrl>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/contracts/${ContractId}/file/${FileId}/presignedUrl`
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  };
}
